export const state = () => ({
  platformUpdates: [],
  platformUpdatesCount: 0,
  activePlatformUpdate: {},
  unviewedPlatformUpdatesCount: 0,
  newUpdateIds: [],
  showNewIndicators: false,
  options: {
    sort: '-releaseDate',
    limit: 50,
    skip: 0,
    search: '',
    page: 0
  }
})

export const getters = {
  getPlatformUpdates: state => state.platformUpdates,
  getPlatformUpdatesCount: state => state.platformUpdatesCount,
  getActivePlatformUpdate: state => state.activePlatformUpdate,
  getOptions: state => state.options,
  getUnviewedPlatformUpdatesCount: state => state.unviewedPlatformUpdatesCount,
  getNewUpdateIds: state => state.newUpdateIds,
  getShowNewIndicators: state => state.showNewIndicators
}

export const actions = {
  async fetchPlatformUpdates({ commit, state, dispatch }) {
    const { sort, limit, page, search } = state.options
    const skip = page * limit
    const params = [
      sort ? `sortby=${sort}` : '',
      limit ? `limit=${limit}` : '',
      skip ? `skip=${skip}` : '',
      search ? `search=${search}` : ''
    ]
    const url = '/platformupdates?' + params.filter(p => p).join('&')
    try {
      const data = await this.$axios.$get(url)
      commit('setPlatformUpdates', data.platformUpdates)
      commit('setPlatformUpdatesCount', data.count)
      await dispatch('computeNewUpdates')
      return true
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'platformUpdates/fetchPlatformUpdates', info: err },
        { root: true }
      )
    }
  },

  computeNewUpdates({ commit, state, rootGetters }) {
    if (!process.client) { return }
    const userId = rootGetters['user/userId']
    if (!userId) { return }
    const key = `lasso_pu_viewed_at_${userId}`
    const lastViewedAt = localStorage.getItem(key)
    let newIds
    if (!lastViewedAt) {
      newIds = state.platformUpdates.map(u => String(u._id))
    } else {
      const cutoff = new Date(lastViewedAt)
      newIds = state.platformUpdates
        .filter(u => u.created && new Date(u.created) > cutoff)
        .map(u => String(u._id))
    }
    commit('setNewUpdateIds', newIds)
    commit('setUnviewedPlatformUpdatesCount', newIds.length)
  },

  markPlatformUpdatesViewed({ commit }) {
    commit('setUnviewedPlatformUpdatesCount', 0)
    commit('setShowNewIndicators', true)
  },

  hideNewIndicators({ commit, rootGetters }) {
    if (process.client) {
      const userId = rootGetters['user/userId']
      if (userId) {
        localStorage.setItem(`lasso_pu_viewed_at_${userId}`, new Date().toISOString())
      }
    }
    commit('setShowNewIndicators', false)
    commit('setNewUpdateIds', [])
    commit('setUnviewedPlatformUpdatesCount', 0)
  },

  async addPlatformUpdate({ commit, dispatch }, update) {
    try {
      const { result, platformUpdate } = await this.$axios.$post('/platformupdate', update)
      if (result === 'ok') {
        commit('addNewPlatformUpdate', platformUpdate)
        return platformUpdate
      }
      return false
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'platformUpdates/addPlatformUpdate', info: err },
        { root: true }
      )
    }
  },

  async updatePlatformUpdate({ commit, dispatch }, update) {
    try {
      const { result, platformUpdate } = await this.$axios.$put(`/platformupdate/${update._id}`, update)
      if (result === 'ok') {
        commit('updatePlatformUpdate', platformUpdate)
        return platformUpdate
      }
      return false
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'platformUpdates/updatePlatformUpdate', info: err },
        { root: true }
      )
    }
  },

  async removePlatformUpdate({ commit, dispatch }, id) {
    try {
      const { result } = await this.$axios.$delete(`/platformupdate/${id}`)
      if (result === 'ok') {
        commit('removePlatformUpdate', id)
      }
      return result === 'ok'
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'platformUpdates/removePlatformUpdate', info: err },
        { root: true }
      )
    }
  },

  async sendPlatformUpdateNotifications({ dispatch }, id) {
    try {
      const data = await this.$axios.$post(`/platformupdate/${id}/notify`)
      return data
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'platformUpdates/sendPlatformUpdateNotifications', info: err },
        { root: true }
      )
    }
  },

  setSearchTerm({ commit }, search) {
    commit('setSearchTerm', search)
  },

  setSortBy({ commit }, sort) {
    commit('setSortBy', sort)
  }
}

export const mutations = {
  setPlatformUpdates(state, updates) {
    state.platformUpdates = updates
  },
  setPlatformUpdatesCount(state, count) {
    state.platformUpdatesCount = count
  },
  addNewPlatformUpdate(state, update) {
    state.platformUpdates.unshift(update)
    state.platformUpdatesCount += 1
  },
  updatePlatformUpdate(state, update) {
    state.platformUpdates = state.platformUpdates
      .map(u => String(u._id) === String(update._id) ? update : u)
  },
  removePlatformUpdate(state, id) {
    state.platformUpdates = state.platformUpdates
      .filter(u => String(u._id) !== String(id))
    state.platformUpdatesCount -= 1
  },
  setUnviewedPlatformUpdatesCount(state, count) {
    state.unviewedPlatformUpdatesCount = count
  },
  setNewUpdateIds(state, ids) {
    state.newUpdateIds = ids
  },
  setShowNewIndicators(state, show) {
    state.showNewIndicators = show
  },
  setSearchTerm(state, search) {
    state.options.page = 0
    state.options.search = search
  },
  setSortBy(state, sort) {
    state.options.sort = sort
  },
  setActivePlatformUpdate(state, update) {
    state.activePlatformUpdate = update
  }
}
